import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api';

type EntityType = 'vehicle' | 'driver' | 'client' | 'site' | 'project' | 'user' | 'advance' | 'collection' | 'trip' | 'flatbed_trip' | 'maintenance_event';

interface EntityLinkProps {
  type: EntityType;
  id: string | null | undefined;
  /** Override the displayed label. If omitted, fetches and shows the entity's code + short name. */
  label?: string | null;
  /** When true, no link is rendered — just the label. Useful when caller is already inside a link/clickable row. */
  noLink?: boolean;
  className?: string;
}

interface EntitySummary {
  code?: string | null;
  name?: string | null;
  phone?: string | null;
  plate?: string | null;
  contact?: string | null;
}

const CACHE = new Map<string, EntitySummary>();
const INFLIGHT = new Map<string, Promise<EntitySummary | null>>();

function routeFor(type: EntityType, id: string): string {
  switch (type) {
    case 'vehicle':           return `/dashboard/vehicles/${id}`;
    case 'driver':            return `/dashboard/drivers/${id}`;
    case 'client':            return `/dashboard/clients/${id}`;
    case 'site':              return `/dashboard/sites/${id}`;
    case 'project':           return `/dashboard/projects/${id}`;
    case 'user':              return `/dashboard/settings/users/${id}`;
    case 'advance':           return `/dashboard/driver-advances-v2/${id}`;
    case 'collection':        return `/dashboard/payment-collections/${id}`;
    case 'trip':              return `/dashboard/trips/${id}`;
    case 'flatbed_trip':      return `/dashboard/flatbed-trips/${id}`;
    case 'maintenance_event': return `/dashboard/maintenance/${id}`;
  }
}

async function fetchSummary(type: EntityType, id: string): Promise<EntitySummary | null> {
  const key = `${type}:${id}`;
  if (CACHE.has(key)) return CACHE.get(key)!;
  if (INFLIGHT.has(key)) return INFLIGHT.get(key)!;
  const p = (async () => {
    try {
      let path: string;
      switch (type) {
        case 'vehicle':           path = `/api/v1/vehicles/${id}`; break;
        case 'driver':            path = `/api/v1/drivers/${id}`; break;
        case 'client':            path = `/api/v1/clients/${id}`; break;
        case 'site':              path = `/api/v1/sites/${id}`; break;
        case 'project':           path = `/api/v1/projects/${id}`; break;
        case 'user':              path = `/api/v1/users/${id}`; break;
        case 'advance':           path = `/api/v1/driver-advances-v2/${id}`; break;
        case 'collection':        path = `/api/v1/payment-collections/${id}`; break;
        case 'flatbed_trip':      path = `/api/v1/flatbed/trips/${id}`; break;
        case 'trip':              path = `/api/v1/trips/${id}`; break;
        case 'maintenance_event': path = `/api/v1/maintenance/events/${id}`; break;
      }
      const r = await apiClient.get<Record<string, unknown>>(path);
      const d = (r.data as Record<string, unknown>);
      const inner = (d.driver ?? d.vehicle ?? d.client ?? d.site ?? d.project ?? d.user ?? d.event ?? d) as Record<string, unknown>;
      const summary: EntitySummary = {
        code:    (inner.code ?? inner.trip_code ?? inner.plate_en) as string | undefined,
        name:    (inner.full_name_en ?? inner.fullNameEn ?? inner.name_en ?? inner.nameEn ?? inner.client_name ?? inner.brand ?? inner.model) as string | undefined,
        phone:   (inner.phone ?? inner.driver_phone) as string | undefined,
        plate:   (inner.plate_en ?? inner.vehicle_plate) as string | undefined,
        contact: (inner.contact_name ?? inner.payer_contact) as string | undefined,
      };
      CACHE.set(key, summary);
      return summary;
    } catch {
      return null;
    } finally {
      INFLIGHT.delete(key);
    }
  })();
  INFLIGHT.set(key, p);
  return p;
}

export function EntityLink({ type, id, label, noLink, className }: EntityLinkProps) {
  const [summary, setSummary] = useState<EntitySummary | null>(null);
  const [hover, setHover] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!id) return;
    const key = `${type}:${id}`;
    if (CACHE.has(key)) { setSummary(CACHE.get(key)!); return; }
    if (label) return; // caller already provided a label; skip the fetch
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchSummary(type, id).then((s) => { if (s) setSummary(s); });
  }, [type, id, label]);

  if (!id) return <span className="text-muted-foreground italic">—</span>;

  const display =
    label ??
    (summary
      ? [summary.code, summary.name].filter(Boolean).join(' · ')
      : 'Loading…');

  const tip = summary
    ? [summary.phone, summary.plate, summary.contact].filter(Boolean).join(' · ')
    : null;

  const linkClass = 'inline-flex items-center gap-1 underline-offset-2 hover:underline text-foreground ' + (className ?? '');

  if (noLink) {
    return <span className={className}>{display}</span>;
  }

  return (
    <span
      className="relative inline-block"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Link to={routeFor(type, id)} className={linkClass}>
        {display === 'Loading…' ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        <span>{display}</span>
        <ExternalLink className="h-3 w-3 opacity-50" />
      </Link>
      {hover && tip && (
        <span className="absolute z-50 left-0 top-full mt-1 px-2 py-1 rounded bg-popover border border-border text-xs text-muted-foreground shadow whitespace-nowrap">
          {tip}
        </span>
      )}
    </span>
  );
}
